import React, { useRef, useState, useContext } from 'react';
import { BiVideo } from 'react-icons/bi';
import Cropper from "../../../../src/cropper/components/CroppersWizard.tsx";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { Language, LoginUserData } from '../../../App';
import { adminBaseURL } from '../../../constants/API';
import ProfileAvatar from '../../common/ProfileAvatar';
import PostImageModal from './PostImageModal';
import { RiImageAddLine, RiSparkling2Fill } from 'react-icons/ri';
import { IoCloseSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import axios from 'axios';
import ImageShowModal from '../../common/ImageShowModal.jsx';
import { useCheckSubscriptionPermission } from '../../../hooks/useCheckSubscriptionPermission';
import MixpanelManager from '../../../constants/MixpanelManager';
import { MixpanelEvents } from '../../../constants/MixpanelEvents';
import './formTheme.css';

const PostTab = ({ onHide, setProgress, handleApiCall }) => {
  const { lang } = useContext(Language);
  const { splashData } = useContext(LoginUserData);
  const checkPermission = useCheckSubscriptionPermission('upload_post');
  const { data: userToken } = JSON.parse(localStorage.getItem("SignupData") || "{}");
  const childRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const maxNumber = 6;
  const [images, setImages] = useState([]);
  const [imagesArr, setImagesArr] = useState([]);
  const [fileUploadRefs, setFileUploadRefs] = useState({});
  const [numberOfImages, setNumberOfImages] = useState(
    Object.keys(images).length < maxNumber
      ? Object.keys(images).length
      : maxNumber
  );
  const [ar, setAR] = useState(undefined);
  const [minARatio, setMinAR] = useState(undefined);
  const [maxARatio, setMaxAR] = useState(undefined);
  const [height, setHeight] = useState(undefined);
  const [width, setWidth] = useState(undefined);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [resizable, setResizable] = useState(true);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [updateCoordinatesCheck, setUpdateCoordinatesCheck] = useState(true);
  const [hasSettings, setHasSettings] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // States for Image Preview in Modal
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const toggleImagePreview = () => setIsImagePreviewOpen(!isImagePreviewOpen);
  const handleImageClick = (img) => {
    setPreviewImage(img);
    toggleImagePreview();
  };

  const handleClose = () => {
    setCropperOpen(false);
  };

  const onImageRemove = (index) => {
    const updatedImages = [...imagesArr];
    const updatedFileUploadRefs = { ...fileUploadRefs };

    updatedImages.splice(index, 1);

    if (fileUploadRefs[index + 1] && fileUploadRefs[index + 1].current) {
      fileUploadRefs[index + 1].current.value = '';
    }

    setImages(updatedImages);
    setImagesArr(updatedImages);

    const newFileUploadRefs = {};
    Object.keys(updatedFileUploadRefs)
      .filter((key) => key !== index.toString())
      .forEach((key, newIndex) => {
        newFileUploadRefs[newIndex] = updatedFileUploadRefs[key];
      });

    setFileUploadRefs(newFileUploadRefs);

    if (updatedImages.length === 0) {
      setHasSettings(true);
      setAR(undefined);
      setMinAR(undefined);
      setMaxAR(undefined);
      setWidth(undefined);
      setHeight(undefined);
      setTop(0);
      setLeft(0);
      setCropperOpen(false);
    }
  };

  const handleImageUploadClick = () => {
    const currentIndex = numberOfImages - 1;
    if (fileUploadRefs[currentIndex] && fileUploadRefs[currentIndex].current) {
      fileUploadRefs[currentIndex].current.value = '';
      fileUploadRefs[currentIndex].current.click();
    }
  };

  const getVideoSource = (src) => {
    const url = URL.createObjectURL(src);
    setVideoSrc(src);
    setVideoUrl(url);
  };

  const handleUpload = async () => {
    if (!videoSrc && imagesArr.length === 0 && caption.trim().length === 0) {
      toast.error(lang?.media_is_required || "Please provide text content or upload media.");
      return;
    }

    try {
      const data = {
        mediaBase64: imagesArr,
        media: [videoSrc],
        type: imagesArr.length > 0 ? "image" : videoSrc ? "video" : "text",
        caption,
      };
      const response = await axios.post(
        adminBaseURL + "user/post/create",
        data,
        {
          onUploadProgress: (progressEvent) => {
            setProgress(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          },
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userToken?.token}`,
          },
        }
      );
      if (response.data.status) {
        MixpanelManager.track(MixpanelEvents.createPost, {
          post_type: imagesArr.length > 0 ? 'image' : videoSrc ? 'video' : 'text',
          media_count: imagesArr.length > 0 ? imagesArr.length : videoSrc ? 1 : 0,
          has_caption: caption.length > 0,
        });
        return response;
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(lang?.failed_upload_please_try_again_later || "Upload failed. Please try again.");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedVideo(file);
      const url = URL.createObjectURL(file);
      setVideoSrc(file);
      setVideoUrl(url);
    }
  };

  const _handleSubmit = async () => {
    if (!checkPermission()) return;
    onHide();
    await handleApiCall(() => handleUpload(), { type: "post" });
  };

  return (
    <>
      <div className="pf-form-body">
        {/* 1. AI Feature Header Banner */}
        <div className="pf-ai-banner">
          <div className="pf-ai-banner-left">
            <RiSparkling2Fill className="pf-ai-banner-sparkle" size={15} />
            <span>Clinical Knowledge & Case Sharing</span>
          </div>
          <span className="pf-ai-banner-tag">Verified Peer Feed</span>
        </div>

        {/* 2. Author Profile Section */}
        <div className="pf-card-section d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <ProfileAvatar
              src={splashData?.user?.image}
              size={46}
              variant="cyan"
              alt={splashData?.user?.name || "Profile"}
            />
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-14 fw-600" style={{ color: "#0B132B" }}>
                  {splashData?.user?.name || "Dr. Medical Professional"}
                </span>
                {splashData?.user?.user_type === "doctor" && (
                  <span
                    style={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      background: "rgba(0, 186, 218, 0.12)",
                      color: "#00BADA",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Specialist
                  </span>
                )}
              </div>
              <span className="fs-12 d-block" style={{ color: "#64748B", marginTop: "1px" }}>
                @{splashData?.user?.user_name || "username"}
              </span>
            </div>
          </div>
        </div>

        {/* 3. Post Content Field */}
        <div className="pf-field">
          <div className="pf-label">
            <span className="pf-label-text">Clinical Insights & Notes</span>
            <span className="pf-optional-badge">Markdown supported</span>
          </div>
          <textarea
            className="pf-textarea"
            placeholder={`${lang?.whats_on_your_mind || "What clinical insights would you like to share"}, ${splashData?.user?.name?.split(' ')[0] || "Doctor"}?`}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={4}
            style={{ fontSize: "14px", lineHeight: "1.6" }}
          />
        </div>

        {/* 4. Dropzone Component (Single unified media upload card) */}
        {imagesArr.length === 0 && !videoUrl && (
          <div className="pf-upload-zone">
            <div className="pf-upload-zone-left">
              <div className="pf-upload-icon-circle">
                <RiImageAddLine size={20} />
              </div>
              <div className="pf-upload-zone-text">
                <div className="pf-upload-zone-title">Add Clinical Images or Video</div>
                <div className="pf-upload-zone-sub">Attach high-res diagnostic cases, OCT scans, or procedural clips</div>
              </div>
            </div>
            <div className="pf-upload-actions d-flex gap-2">
              <button
                type="button"
                className="pf-chip-btn"
                onClick={handleImageUploadClick}
              >
                <RiImageAddLine size={15} />
                <span>{lang?.images || "Images"}</span>
              </button>
              <button
                type="button"
                className="pf-chip-btn"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "video/*";
                  input.addEventListener("change", handleFileChange);
                  input.click();
                }}
              >
                <BiVideo size={15} />
                <span>Video</span>
              </button>
            </div>
          </div>
        )}

        {/* 5. Media Preview Section (When media is attached) */}
        {(imagesArr.length > 0 || videoUrl) && (
          <div className="pf-card-section">
            <div className="pf-thumb-strip">
              {imagesArr.length > 0 && (
                <div className="d-flex gap-2">
                  {imagesArr.map((img, index) => (
                    <div
                      key={index}
                      className="pf-thumb"
                      style={{ width: "115px", height: "115px" }}
                    >
                      <PhotoProvider speed={() => 800}>
                        <PhotoView src={img}>
                          <img
                            className="cursor"
                            onClick={() => handleImageClick(img)}
                            src={img}
                            alt="Uploaded"
                            style={{
                              width: "115px",
                              height: "115px",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </PhotoView>
                      </PhotoProvider>
                      <button
                        type="button"
                        className="pf-thumb-remove"
                        aria-label="Remove"
                        onClick={() => onImageRemove(index)}
                      >
                        <IoCloseSharp size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {videoUrl && (
                <div className="pf-thumb" style={{ flexShrink: 0, maxWidth: "220px" }}>
                  <video
                    src={videoUrl}
                    style={{
                      height: "115px",
                      width: "100%",
                      display: "block",
                    }}
                    controls
                  />
                  <button
                    type="button"
                    className="pf-thumb-remove"
                    aria-label="Remove"
                    onClick={() => {
                      setVideoSrc(null);
                      setVideoUrl(null);
                      setSelectedVideo(null);
                    }}
                  >
                    <IoCloseSharp size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {!videoUrl && (
          <Cropper
            images={images}
            setImages={setImages}
            imagesArr={imagesArr}
            setImagesArr={setImagesArr}
            max={maxNumber}
            allowCrop={true}
            ar={ar}
            minARatio={minARatio}
            maxARatio={maxARatio}
            setAR={setAR}
            setMaxAR={setMaxAR}
            setMinAR={setMinAR}
            resizable={resizable}
            setResizable={setResizable}
            height={height}
            setHeight={setHeight}
            width={width}
            setWidth={setWidth}
            top={top}
            setTop={setTop}
            left={left}
            setLeft={setLeft}
            hasSettings={hasSettings}
            setHasSettings={setHasSettings}
            updateCoordinatesCheck={updateCoordinatesCheck}
            setUpdateCoordinatesCheck={setUpdateCoordinatesCheck}
            handleClose={handleClose}
            fileUploadRefs={fileUploadRefs}
            setFileUploadRefs={setFileUploadRefs}
            numberOfImages={numberOfImages}
            setNumberOfImages={setNumberOfImages}
            cropperOpen={cropperOpen}
            setCropperOpen={setCropperOpen}
          />
        )}

        {/* 6. Footer Action Bar (Clean cancel & submit only) */}
        <div className="pf-footer-row" style={{ justifyContent: "space-between" }}>
          {imagesArr.length > 0 && (
            <button
              type="button"
              onClick={handleImageUploadClick}
              disabled={imagesArr.length >= 6}
              className="pf-chip-btn"
            >
              <RiImageAddLine size={15} />
              <span>Add More Images ({imagesArr.length}/6)</span>
            </button>
          )}

          <div className="pf-footer-actions">
            <button
              type="button"
              className="pf-btn-cancel"
              onClick={onHide}
            >
              {lang?.cancel || "Cancel"}
            </button>
            <button
              type="button"
              className="pf-btn-submit"
              disabled={!(imagesArr.length > 0 || videoSrc || caption.trim().length > 0)}
              onClick={_handleSubmit}
            >
              <span>{lang?.upload || "Publish Post"}</span>
            </button>
          </div>
        </div>
      </div>

      <PostImageModal
        ref={childRef}
        getVideoSource={getVideoSource}
        showModal={showImageModal}
        setShowModal={setShowImageModal}
      />
      <ImageShowModal
        isOpen={isImagePreviewOpen}
        toggle={toggleImagePreview}
        imageSrc={previewImage}
      />
    </>
  );
};

export default PostTab;
